import { createContext, useContext, useState, useCallback, useRef } from "react";
import type { Product } from "@/types/product";

const MAX_COMPARE = 3;
const MAX_HISTORY = 5;

interface CompareContextValue {
  compareList: Product[];
  compareHistory: Product[];
  isInCompare: (id: string) => boolean;
  toggleCompare: (product: Product) => void;
  removeFromCompare: (id: string) => void;
  clearCompare: () => void;
  canAdd: boolean;
  modalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const CompareContext = createContext<CompareContextValue | null>(null);

export function CompareProvider({ children }: { children: React.ReactNode }) {
  const [compareList, setCompareList] = useState<Product[]>([]);
  const [compareHistory, setCompareHistory] = useState<Product[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Keep a ref so toggleCompare can read latest list without stale closure
  const compareListRef = useRef<Product[]>([]);
  compareListRef.current = compareList;

  const isInCompare = useCallback((id: string) => compareListRef.current.some((p) => p.id === id), []);

  const toggleCompare = useCallback((product: Product) => {
    const prev = compareListRef.current;
    const exists = prev.some((p) => p.id === product.id);

    if (exists) {
      setCompareList(prev.filter((p) => p.id !== product.id));
    } else if (prev.length < MAX_COMPARE) {
      setCompareList([...prev, product]);
      // Record in history (newest first, no duplicates, max 5)
      setCompareHistory((hist) => {
        if (hist.some((p) => p.id === product.id)) return hist;
        return [product, ...hist].slice(0, MAX_HISTORY);
      });
    }
  }, []);

  const removeFromCompare = useCallback((id: string) => {
    setCompareList((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareList([]);
    setModalOpen(false);
  }, []);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const canAdd = compareList.length < MAX_COMPARE;

  return (
    <CompareContext.Provider
      value={{
        compareList,
        compareHistory,
        isInCompare,
        toggleCompare,
        removeFromCompare,
        clearCompare,
        canAdd,
        modalOpen,
        openModal,
        closeModal,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error("useCompare must be used within CompareProvider");
  return ctx;
}
